import { useState, useRef, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useSnapshot } from 'valtio';
import { authState, authActions } from '../../stores/authStore';
import { switchCompanyData } from '../../utils/loadData';
import { getTheme, setTheme, type Theme } from '../../utils/theme';

const roleLabels: Record<string, string> = {
  super_admin: 'Super Admin',
  company_admin: 'Company Admin',
  company_user: 'Team Member',
};

export function UserProfileMenu() {
  const { user, companies, isSwitchingCompany } = useSnapshot(authState);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [theme, setThemeState] = useState<Theme>(() => getTheme());

  function pickTheme(next: Theme) {
    setTheme(next);
    setThemeState(next);
  }

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!user) return null;

  const initials = `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}`.toUpperCase();
  const hasMultipleCompanies = companies.length > 1;

  async function handleLogout() {
    setOpen(false);
    await authActions.logout();
    navigate({ to: '/login' });
  }

  async function handleSwitchCompany(companyId: string) {
    if (companyId === user?.companyId || isSwitchingCompany) return;
    try {
      await authActions.switchCompany(companyId);
      await switchCompanyData();
      setOpen(false);
    } catch (err) {
      console.error('Failed to switch company:', err);
    }
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors"
        style={{ color: 'var(--color-text)' }}
        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-muted)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      >
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white"
          style={{ background: 'var(--color-accent)' }}
        >
          {initials}
        </div>
        <div className="text-left hidden sm:block">
          <p className="text-sm font-medium leading-tight">
            {user.firstName} {user.lastName}
          </p>
          <p className="text-xs leading-tight" style={{ color: 'var(--color-text-muted)' }}>
            {roleLabels[user.role] || user.role}
          </p>
        </div>
        <svg className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          {/* User info */}
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900">
              {user.firstName} {user.lastName}
            </p>
            <p className="text-xs text-gray-500 truncate">{user.email}</p>
            {user.companyName && (
              <p className="text-xs text-purple-600 mt-1 font-medium">{user.companyName}</p>
            )}
          </div>

          {/* Company switcher */}
          {hasMultipleCompanies && (
            <div className="border-b border-gray-100">
              <p className="px-4 pt-2 pb-1 text-xs font-medium text-gray-400 uppercase tracking-wide">
                Switch Company
              </p>
              {isSwitchingCompany ? (
                <div className="px-4 py-3 flex items-center gap-2 text-sm text-gray-500">
                  <div className="w-4 h-4 border-2 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
                  Switching...
                </div>
              ) : (
                companies.map((company) => {
                  const isActive = company.id === user.companyId;
                  return (
                    <button
                      key={company.id}
                      onClick={() => handleSwitchCompany(company.id)}
                      disabled={isActive}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors flex items-center justify-between ${
                        isActive
                          ? 'bg-purple-50 text-purple-700 cursor-default'
                          : 'text-gray-700 hover:bg-gray-50 cursor-pointer'
                      }`}
                    >
                      <div>
                        <p className="font-medium">{company.name}</p>
                        <p className="text-xs text-gray-400">{roleLabels[company.role] || company.role}</p>
                      </div>
                      {isActive && (
                        <svg className="w-4 h-4 text-purple-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          )}

          {/* Theme picker — design system §10 (light/dark/system) */}
          <div className="border-b border-gray-100">
            <p className="px-4 pt-2 pb-1 text-xs font-medium text-gray-400 uppercase tracking-wide">
              Theme
            </p>
            <div className="px-3 pb-2 flex gap-1">
              {(['light', 'dark', 'system'] as const).map((opt) => {
                const isActive = theme === opt;
                return (
                  <button
                    key={opt}
                    onClick={() => pickTheme(opt)}
                    className="flex-1 py-1.5 px-2 text-xs font-medium rounded-md transition-colors capitalize"
                    style={{
                      background: isActive ? 'var(--color-accent-subtle)' : 'transparent',
                      color: isActive ? 'var(--color-accent)' : 'var(--color-text-muted)',
                      border: '1px solid',
                      borderColor: isActive ? 'var(--color-accent)' : 'var(--color-border)',
                    }}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <button
            onClick={() => { setOpen(false); navigate({ to: '/profile' }); }}
            className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            My Profile
          </button>
          <button
            onClick={handleLogout}
            className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
}
